import React, { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { format, parseISO } from "date-fns";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  stat: {
    padding: theme.spacing(1),
    borderRadius: theme.borderRadius.small,
    backgroundColor: theme.palette.tintedBackground,
    textAlign: "center",
  },
  statValue: {
    fontSize: 22,
    fontWeight: 600,
  },
  pill: {
    display: "inline-block",
    padding: "1px 8px",
    borderRadius: theme.borderRadius.pill,
    fontSize: 12,
    color: theme.palette.common.white,
  },
  pending: { backgroundColor: theme.palette.grey[500] },
  sent: { backgroundColor: theme.palette.success.main },
  error: { backgroundColor: theme.palette.error.main },
  skipped: { backgroundColor: theme.palette.warning.main },
  detail: {
    maxWidth: 320,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

const FILTERS = ["all", "pending", "sent", "error", "skipped"];

const CampaignReportModal = ({ open, onClose, campaign }) => {
  const classes = useStyles();
  const [filter, setFilter] = useState("all");
  const [shippings, setShippings] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);

  const fetchPage = useCallback(
    async (pageNumber) => {
      if (!campaign) return;
      try {
        const { data } = await api.get(`/campaigns/${campaign.id}/shippings`, {
          params: { status: filter === "all" ? undefined : filter, pageNumber },
        });
        setShippings((prev) =>
          pageNumber === 1 ? data.shippings : [...prev, ...data.shippings]
        );
        setHasMore(data.hasMore);
        setPage(pageNumber);
      } catch (err) {
        toastError(err);
      }
    },
    [campaign, filter]
  );

  useEffect(() => {
    if (open) fetchPage(1);
  }, [open, fetchPage]);

  // Acompanha o envio ao vivo
  useEffect(() => {
    if (!open || !campaign) return undefined;
    const socket = openSocket();
    socket.on("campaign", (data) => {
      if (data.campaign?.id === campaign.id) fetchPage(1);
    });
    return () => socket.disconnect();
  }, [open, campaign, fetchPage]);

  if (!campaign) return null;
  const { stats } = campaign;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>{i18n.t("campaignReport.title", { name: campaign.name })}</DialogTitle>
      <DialogContent dividers>
        <div className={classes.stats}>
          {["total", "sent", "pending", "error", "skipped"].map((key) => (
            <div key={key} className={classes.stat}>
              <div className={classes.statValue}>{stats[key]}</div>
              <Typography variant="caption">
                {key === "total"
                  ? i18n.t("campaignReport.total")
                  : i18n.t(`campaignReport.filters.${key}`)}
              </Typography>
            </div>
          ))}
        </div>
        <Tabs
          value={filter}
          onChange={(e, value) => setFilter(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
        >
          {FILTERS.map((f) => (
            <Tab key={f} value={f} label={i18n.t(`campaignReport.filters.${f}`)} />
          ))}
        </Tabs>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("campaignReport.table.contact")}</TableCell>
              <TableCell align="center">{i18n.t("campaignReport.table.status")}</TableCell>
              <TableCell align="center">{i18n.t("campaignReport.table.sentAt")}</TableCell>
              <TableCell>{i18n.t("campaignReport.table.detail")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shippings.map((s) => (
              <TableRow key={s.id}>
                <TableCell>
                  {s.contact?.name}
                  <Typography variant="caption" component="div" color="textSecondary">
                    {s.contact?.number}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <span className={clsx(classes.pill, classes[s.status])}>
                    {i18n.t(`campaignReport.status.${s.status}`)}
                  </span>
                </TableCell>
                <TableCell align="center">
                  {s.sentAt ? format(parseISO(s.sentAt), "dd/MM HH:mm") : "-"}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" className={classes.detail} title={s.errorMessage || s.body || ""}>
                    {s.errorMessage || s.body || "-"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {hasMore && (
          <Button size="small" onClick={() => fetchPage(page + 1)}>
            {i18n.t("campaignReport.loadMore")}
          </Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {i18n.t("campaignReport.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignReportModal;
